import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Users, Code, Brain, Award, MessageSquare, Calculator, Play, Calendar, Clock, AlertTriangle, Building2, FileText, Star, CheckCircle2, Rocket, Download } from 'lucide-react';
import { getDesktopAppDownloadUrl, getPlatform, isDesktopApp, startDesktopProctoring } from "../../utils/desktopBridge.js";
import DesktopLoader from "./DesktopLoader";
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from '@/lib/utils';
import { flowMap, normalizeTestType, getTestsForCategory as getTestsForCategoryUtil } from '../utils/testCategories';
import {
  convertToIST,
  isTestExpired,
  isTestAvailableNow,
  getTimeRemaining
} from '../utils/testTimeUtils';

const TestCard = ({ tests, allTests = [], setSelectedTestType, selectedStatus = "all", setSelectedStatus }) => {
  const [activeTab, setActiveTab] = useState("t");
  const [showPopup, setShowPopup] = useState(false);
  const [showPopup2, setShowPopup2] = useState(false);
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(null);
  const [isStartingTest, setIsStartingTest] = useState(false);
  const navigate = useNavigate();
  const [showDesktopLoader, setShowDesktopLoader] = useState(false);
  const [loaderText, setLoaderText] = useState("Preparing your test…");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.showTestSubmittedPopup) {
      setShowPopup(true);
      window.history.replaceState({}, document.title);
    }
  }, []);

  const testCategories = [
    { id: "t", name: "Screening", icon: Users },
    { id: "c", name: "Coding", icon: Code },
    { id: "p", name: "Personality", icon: Brain },
    { id: "b", name: "Behavioral", icon: Award },
    { id: "o", name: "Communication", icon: MessageSquare },
    { id: "apt", name: "Aptitude", icon: Calculator },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedTestType(tabId);
  };

  // Use getTimeRemaining with prefix for TestCard
  const getTimeRemainingMessage = (scheduledTimeStr) => {
    return getTimeRemaining(scheduledTimeStr, true);
  };

  const needsDesktopDownload = (test) => {
    const platform = String(test?.test_platform || "web").toLowerCase();
    return platform === "desktop" && !isDesktopApp();
  };

  const openDesktopDownload = async () => {
    if (downloadLoading) return;

    setDownloadLoading(true);
    try {
      const downloadInfo = await getDesktopAppDownloadUrl();
      window.open(downloadInfo.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error('[DOWNLOAD] Error:', error);
      alert(error.message || 'Failed to get download link. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    try {
      await fetch('/api/submit-rating', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating })
      });
    } catch (error) { }
    setShowPopup2(true);
    setShowPopup(false);
    setTimeout(() => {
      window.location.reload();
      setShowPopup2(false);
      setShowPopup(false);
    }, 3000);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    window.location.reload();
  };

  const isChromeOrChromiumBrowser = () => {
    if (isDesktopApp()) return true;
    const userAgent = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    const isEdge = userAgent.includes('Edg/') || userAgent.includes('Edge/');
    const isOpera = userAgent.includes('OPR/') || userAgent.includes('Opera') || userAgent.includes('OPT/');
    const isChromium = userAgent.includes('Chromium');
    const isVivaldi = userAgent.includes('Vivaldi');
    const isSamsung = userAgent.includes('SamsungBrowser') || userAgent.includes('SAMSUNG');
    const isUC = userAgent.includes('UCBrowser') || userAgent.includes('UBrowser');
    const isYandex = userAgent.includes('YaBrowser') || userAgent.includes('Yandex');
    const hasSpeechSynthesis = 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
    const hasSpeechRecognition = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
    const isGenuineChrome = userAgent.includes('Chrome/') && vendor === 'Google Inc.' && !isEdge && !isOpera && !isChromium && !isVivaldi && !isSamsung && !isUC && !isYandex;
    const isGenuineEdge = isEdge && userAgent.includes('Chrome/') && userAgent.includes('Edg/');
    return (isGenuineChrome || isGenuineEdge) && hasSpeechSynthesis && hasSpeechRecognition;
  };

  const handleStartTest = async (test) => {
    if (needsDesktopDownload(test)) {
      openDesktopDownload();
      return;
    }
    if (!isChromeOrChromiumBrowser()) {
      setShowBrowserWarning(true);
      return;
    }
    setSelectedTest(test);
    setShowConfirmPopup(true);
  };

  const handleConfirmStartTest = async () => {
    if (!selectedTest || isStartingTest) return;
    setIsStartingTest(true);
    try {
      if (needsDesktopDownload(selectedTest)) {
        openDesktopDownload();
        setShowConfirmPopup(false);
        return;
      }
      await fetch("/api/mark_completed", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ testId: selectedTest.test_id, testType: selectedTest.test_type })
      });
      sessionStorage.setItem("isValidAccess", "true");

      if (isDesktopApp()) {
        setLoaderText("Initializing secure environment…");
        setShowDesktopLoader(true);
        await new Promise(r => setTimeout(r, 50));
        setLoaderText("Starting proctoring system…");
        const success = await startDesktopProctoring(selectedTest.test_id, selectedTest.test_type);
        if (!success) {
          await new Promise(r => setTimeout(r, 500));
          setLoaderText("Proctoring active. Loading instructions…");
        }
        navigate(`/instructions?test_id=${selectedTest.test_id}`);
        setShowDesktopLoader(false);
        return;
      }

      navigate(`/instructions?test_id=${selectedTest.test_id}`);
      setShowConfirmPopup(false);
    } catch (error) {
      setIsStartingTest(false);
      setShowDesktopLoader(false);
    } finally {
      setSelectedTest(null);
    }
  };

  const handleCancelStartTest = () => {
    setShowConfirmPopup(false);
    setSelectedTest(null);
    setIsStartingTest(false);
  };


  const getTestDisplayName = (test) => {
    const typeMap = {
      c: "Coding",
      t: "Screening",
      p: "Personality",
      b: "Behavioral",
      o: "Communication",
      a: "Personality & Behavioral",
      rs: "Realtime Screening",
      rpnb: "Realtime P&B",
      ro: "Realtime Communication",
      qc: "Quick Coding",
      qt: "Quick Screening",
      qa: "Quick P&B",
      apt: "Aptitude",
      fscc: "Screening + Communication + Coding"
    };
    const tt = String(test.test_type || '').toLowerCase();
    if (typeMap[tt]) return typeMap[tt];
    const base = normalizeTestType(tt);
    if (typeMap[base]) return typeMap[base];
    const flowTypes = flowMap[tt];
    if (flowTypes && flowTypes.length > 0) {
      const typeNames = flowTypes.map((id) => typeMap[id]).filter(Boolean);
      return typeNames.join(" + ");
    }
    return "Test";
  };

  const getStatus = (test) => {
    const userEmail = localStorage.getItem("user_email");
    const isPrivilegedUser = userEmail === "anubhav.mishra@newzenalpha.com" || userEmail === "anubhav.mishra303@gmail.com";
    if (isPrivilegedUser) return "upcoming";
    if (test.test_status === "in_progress") return "in_progress";
    if (test.test_status === "completed") return "completed";
    if (isTestExpired(test.scheduled_time)) return "expired";
    if (isTestAvailableNow(test.scheduled_time)) return "upcoming";
    return "pending";
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: "success",
      expired: "error",
      in_progress: "info",
      upcoming: "warning",
      pending: "muted",
    };
    const labels = {
      completed: "Completed",
      expired: "Expired",
      in_progress: "In Progress",
      upcoming: "Ready",
      pending: "Scheduled",
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  // Filter tests for a specific category
  const getTestsForCategory = (categoryId) => {
    return getTestsForCategoryUtil(allTests, categoryId);
  };

  // Get indicator status for a category
  const getTabIndicator = (categoryId) => {
    const categoryTests = getTestsForCategory(categoryId);

    if (categoryTests.length === 0) return null;

    // Check if all tests are completed
    const allCompleted = categoryTests.every(test => test.test_status === 'completed');
    if (allCompleted) return null;

    // Check for in_progress tests (highest priority)
    const hasInProgress = categoryTests.some(test => test.test_status === 'in_progress');
    if (hasInProgress) return 'in_progress';

    // Check for ready/upcoming tests
    const hasReady = categoryTests.some(test => {
      const status = getStatus(test);
      return status === 'upcoming';
    });
    if (hasReady) return 'ready';

    // Check for scheduled/pending tests
    const hasScheduled = categoryTests.some(test => {
      const status = getStatus(test);
      return status === 'pending';
    });
    if (hasScheduled) return 'scheduled';

    return null;
  };

  return (
    <Card className="border shadow-sm overflow-hidden bg-background">
      {/* Tab Navigation with Status Filter */}
      <div className="border-b border-border bg-background px-3 py-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Test Type Tabs */}
          <div className="flex gap-1 overflow-x-auto scrollbar-hide flex-1 min-w-0">
            {testCategories.map((category) => {
              const Icon = category.icon;
              const indicator = getTabIndicator(category.id);
              return (
                <button
                  key={category.id}
                  onClick={() => handleTabChange(category.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium leading-normal transition-all whitespace-nowrap relative",
                    activeTab === category.id
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="hidden sm:inline">{category.name}</span>
                  {indicator && (
                    <span
                      className={cn(
                        "w-2 h-2 rounded-full shrink-0",
                        indicator === "in_progress" && "bg-info animate-pulse",
                        indicator === "ready" && "bg-warning",
                        indicator === "scheduled" && "bg-muted-foreground"
                      )}
                      title={
                        indicator === "in_progress" ? "Test in progress" :
                          indicator === "ready" ? "Test ready to start" :
                            indicator === "scheduled" ? "Test scheduled" : ""
                      }
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-xs sm:text-sm font-medium text-muted-foreground whitespace-nowrap hidden sm:inline">
              Status:
            </label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[140px] sm:w-[160px] h-8 text-xs sm:text-sm">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="upcoming">Ready</SelectItem>
                <SelectItem value="pending">Scheduled</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Test Cards Grid */}
      <CardContent className="p-3 sm:p-4">
        {tests.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {tests.map((test) => {
              const status = getStatus(test);
              const timeRemaining = getTimeRemainingMessage(test.scheduled_time);

              return (
                <div
                  key={test.test_id}
                  className={cn(
                    "group relative p-3 rounded-lg border bg-background transition-all flex flex-col",
                    "hover:shadow-md hover:border-primary/30",
                    status === "completed" && "bg-success/20 border-success/40",
                    status === "expired" && "bg-error/20 border-error/40 opacity-60",
                  )}
                >
                  {/* Header - Compact */}
                  <div className="flex items-start justify-between gap-2 mb-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                        <h3 className="font-semibold text-sm leading-snug truncate">{test.company_name}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed truncate mt-0.5">{test.topic}</p>
                    </div>
                    <div className="shrink-0">{getStatusBadge(status)}</div>
                  </div>

                  {/* Meta Info - More Compact */}
                  <div className="space-y-1.5 mb-3 flex-1">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground leading-normal">
                      <Calendar className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{convertToIST(test.scheduled_time)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground leading-normal">
                      <FileText className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{getTestDisplayName(test)}</span>
                    </div>
                  </div>

                  {/* Footer - Compact */}
                  <div className="flex items-center justify-between pt-2.5 border-t border-border gap-2">
                    <span className="text-xs text-muted-foreground font-mono truncate opacity-75">
                      #{test.test_id}
                    </span>

                    {status === "upcoming" && (
                      needsDesktopDownload(test) ? (
                        <Button
                          size="sm"
                          onClick={openDesktopDownload}
                          disabled={downloadLoading}
                          className="h-7 text-xs gap-1.5 shrink-0"
                        >
                          <Download className="w-3 h-3" />
                          {downloadLoading ? 'Loading...' : 'Download'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          disabled={!isTestAvailableNow(test.scheduled_time)}
                          onClick={() => handleStartTest(test)}
                          className="h-7 text-xs gap-1.5 shrink-0"
                        >
                          <Play className="w-3 h-3" />
                          Start
                        </Button>
                      )
                    )}

                    {status === "pending" && timeRemaining && (
                      <span className="text-xs text-warning font-medium truncate text-right leading-normal">
                        Starts {timeRemaining}
                      </span>
                    )}

                    {status === "completed" && (
                      <span className="text-xs text-success font-medium shrink-0 leading-normal">Done</span>
                    )}

                    {status === "expired" && (
                      <span className="text-xs text-error font-medium shrink-0 leading-normal">Missed</span>
                    )}

                    {status === "in_progress" && (
                      needsDesktopDownload(test) ? (
                        <Button
                          size="sm"
                          onClick={openDesktopDownload}
                          disabled={downloadLoading}
                          className="h-7 text-xs gap-1.5 shrink-0"
                        >
                          <Download className="w-3 h-3" />
                          {downloadLoading ? 'Loading...' : 'Download'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleStartTest(test)}
                          className="h-7 text-xs gap-1.5 shrink-0"
                        >
                          <Play className="w-3 h-3" />
                          Continue
                        </Button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-2">
              <FileText className="w-5 h-5 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-sm text-foreground mb-1 leading-normal">No Tests Available</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">No tests scheduled for this category.</p>
          </div>
        )}
      </CardContent>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmPopup} onOpenChange={(open) => !open && handleCancelStartTest()}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader className="space-y-1.5">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
              <Rocket className="h-9 w-9 text-primary" />
            </div>
            <AlertDialogTitle className="text-center text-lg font-semibold">
              Ready to Start?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-sm text-muted-foreground">
              This test can only be attempted <strong className="font-semibold text-foreground">once</strong>. Make sure you're ready before proceeding.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-row sm:justify-center gap-2">
            <AlertDialogCancel onClick={handleCancelStartTest} className="mt-0 border-2 border-border">
              Not Yet
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmStartTest}
              disabled={isStartingTest}
            >
              {isStartingTest ? "Starting..." : "Start Test"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showDesktopLoader && <DesktopLoader text={loaderText} />}

      {/* Rating Dialog */}
      <Dialog open={showPopup} onOpenChange={(open) => !open && handleClosePopup()}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle>Test Submitted!</DialogTitle>
            <DialogDescription>How was your experience?</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-1 py-4">
            {[...Array(5)].map((_, index) => {
              const starValue = index + 1;
              const isFilled = starValue <= (hover || rating);
              return (
                <Star
                  key={index}
                  size={28}
                  className={cn(
                    "cursor-pointer transition-transform hover:scale-110",
                    isFilled ? "fill-warning text-warning" : "fill-muted text-muted-foreground/30"
                  )}
                  onClick={() => setRating(starValue)}
                  onMouseEnter={() => setHover(starValue)}
                  onMouseLeave={() => setHover(null)}
                />
              );
            })}
          </div>
          <Button onClick={handleSubmitRating} className="w-full">Submit Rating</Button>
        </DialogContent>
      </Dialog>

      {/* Thank You Dialog */}
      <Dialog open={showPopup2} onOpenChange={setShowPopup2}>
        <DialogContent className="max-w-xs text-center">
          <div className="flex justify-center mb-3">
            <CheckCircle2 className="w-10 h-10 text-success" />
          </div>
          <DialogTitle>Thank You!</DialogTitle>
          <DialogDescription>We appreciate your feedback.</DialogDescription>
        </DialogContent>
      </Dialog>

      {/* Browser Warning Dialog */}
      <Dialog open={showBrowserWarning} onOpenChange={setShowBrowserWarning}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center mb-3">
              <AlertTriangle className="w-6 h-6 text-warning" />
            </div>
            <DialogTitle>Browser Not Supported</DialogTitle>
            <DialogDescription>
              Please use <strong>Google Chrome</strong> or <strong>Microsoft Edge</strong> for the best experience.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              onClick={() => window.open('https://www.google.com/chrome/', '_blank')}
              className="flex-1"
            >
              Get Chrome
            </Button>
            <Button variant="outline" onClick={() => setShowBrowserWarning(false)} className="flex-1">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TestCard;
